import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  House,
  Users,
  ShoppingCart,
  Truck,
  Package,
  FileText,
  Gear,
  SignOut,
  Sun,
  Moon,
  List,
  ChartBar,
  Warehouse,
  MapPin,
  ChartLine,
  Globe,
  Storefront,
  DeviceMobile
} from '@phosphor-icons/react';

// IMS Logo URL
const IMS_LOGO_URL = "/IMSLOGO-2.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: House, roles: ['admin', 'super_admin', 'telesales', 'planner', 'dispatcher', 'depot_ops', 'finance', 'customer'] },
  { path: '/driver', label: 'Driver App', icon: DeviceMobile, roles: ['driver'] },
  { path: '/portal', label: 'Portal', icon: Storefront, roles: ['admin', 'super_admin', 'customer', 'telesales'] },
  { path: '/customers', label: 'Customers', icon: Users, roles: ['admin', 'super_admin', 'telesales', 'finance'] },
  { path: '/orders', label: 'Orders', icon: ShoppingCart, roles: ['admin', 'super_admin', 'telesales', 'planner'] },
  { path: '/planning', label: 'Planning', icon: MapPin, roles: ['admin', 'super_admin', 'planner', 'dispatcher'] },
  { path: '/dispatch', label: 'Dispatch', icon: Truck, roles: ['admin', 'super_admin', 'dispatcher', 'depot_ops'] },
  { path: '/deliveries', label: 'Deliveries', icon: Package, roles: ['admin', 'super_admin', 'depot_ops'] },
  { path: '/maps', label: 'Maps', icon: Globe, roles: ['admin', 'super_admin', 'planner', 'dispatcher'] },
  { path: '/invoices', label: 'Invoices', icon: FileText, roles: ['admin', 'super_admin', 'finance'] },
  { path: '/analytics', label: 'Analytics', icon: ChartLine, roles: ['admin', 'super_admin', 'finance'] },
  { path: '/reports', label: 'Reports', icon: ChartBar, roles: ['admin', 'super_admin', 'finance'] },
  { path: '/admin', label: 'Admin', icon: Gear, roles: ['admin', 'super_admin'] },
];

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Navy */}
      <aside className="w-64 bg-navy-950 text-slate-300 fixed h-full flex flex-col border-r border-navy-900">
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-navy-900">
          <div className="flex items-center gap-3">
            <img 
              src={IMS_LOGO_URL} 
              alt="IMS Logo" 
              className="h-10 w-auto"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                  isActive
                    ? 'bg-orange-500/20 text-orange-400 font-medium'
                    : 'hover:bg-navy-800 text-slate-400 hover:text-slate-200'
                }`
              }
            >
              <item.icon weight="duotone" className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-navy-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-sm font-medium text-white">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate capitalize">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-navy-950 border-b border-slate-200 dark:border-navy-800 sticky top-0 z-40 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-lg font-bold text-foreground">
              Tankerbase Next
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu">
                  <List className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {user?.role?.replace('_', ' ')}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Gear className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <SignOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
